import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Spinner, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { generateSlug } from "../utils/seoUtils";
import FilterPanel from "../components/KeywordTrackingNewUIPage/FilterPanel";
import AppsTrackingPlanModal from '../components/Common/Modal/AppsTrackingPlanModal';
import PlatformTabs from '../components/Common/PlatformTabs';
import { useFilters } from '../hooks/useFilters';
import { usePlatforms } from '../contexts/PlatformsContext';
import { blogApiUrl } from '../utils/variables';
// Backend API endpoint
const CATEGORY_API = `${blogApiUrl}/tracker/explorer/category`;

// BannerCarousel component for top banners
function BannerCarousel({ apps }) {
  // Only use the first 3 banners
  const banners = apps.slice(0, 3);
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef();
  const DURATION = 3500;

  // Autoplay effect
  React.useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, DURATION);
    return () => clearTimeout(timeoutRef.current);
  }, [current, banners.length]);

  const goTo = (idx) => setCurrent(idx);
  const goLeft = () => setCurrent((idx) => (idx === 0 ? banners.length - 1 : idx - 1));
  const goRight = () => setCurrent((idx) => (idx + 1) % banners.length);

  const app = banners[current];

  if (!app) return null;
  const formattedRating =
    typeof app.rating === "number" && Number.isFinite(app.rating) ? app.rating.toFixed(1) : null;

  const genreSlug = Array.isArray(app.genre) && app.genre.length > 0 
    ? generateSlug(app.genre[0]) 
    : app.genre && typeof app.genre === 'string' 
      ? generateSlug(app.genre) 
      : "app";
  const nameSlug = generateSlug(app.title);
  const appLink = `/xr/${genreSlug}/${nameSlug}?id=${app.id || app.item_id}`;

  return (
    <div className="banner-carousel mb-4" style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent' }}>
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'transform 0.5s cubic-bezier(.4,2,.6,1)' }}>
        <Link to={appLink} key={app.id} className="banner-slide" style={{ width: '90vw', maxWidth: 1200, borderRadius: 32, overflow: 'hidden', background: '#23242b', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', paddingLeft: 0, paddingRight: 0, cursor: 'pointer' }}>
          <img src={app.thumbnail} alt={app.title} className="banner-image" style={{ width: '100%', objectFit: 'cover', borderRadius: 32, maxWidth: '100%' }} />
        </Link>
        {/* Navigation arrows centered at the bottom */}
        <div style={{ position: 'absolute', bottom: 32, left: 0, width: '100%', display: 'flex', justifyContent: 'center', gap: 32, zIndex: 3 }}>
          <button
            className="banner-arrow left"
            onClick={goLeft}
            aria-label="Scroll banners left"
          >&#8592;</button>
          <button
            className="banner-arrow right"
            onClick={goRight}
            aria-label="Scroll banners right"
          >&#8594;</button>
        </div>
        {/* Slider dots */}
        <div style={{ position: 'absolute', bottom: 16, left: 0, width: '100%', display: 'flex', justifyContent: 'center', gap: 10, zIndex: 3 }}>
          {banners.map((_, idx) => (
            <span key={idx} onClick={() => goTo(idx)} style={{ width: 14, height: 14, borderRadius: '50%', background: idx === current ? '#F6255A' : '#fff', opacity: idx === current ? 1 : 0.5, cursor: 'pointer', border: '2px solid #F6255A', display: 'inline-block' }} />
          ))}
        </div>
      </div>
      <style>{`
        .banner-arrow {
          background: #23242b;
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          font-size: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.85;
          transition: background 0.18s;
        }
        .banner-arrow:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .banner-slide, .banner-image {
          height: 400px;
        }
        .banner-content {
          left: 40px;
          bottom: 40px;
          min-width: 260px;
        }
        .banner-title {
          font-size: 32px;
        }
        .banner-subtitle {
          font-size: 20px;
        }
        .banner-rating {
          font-size: 18px;
        }
        @media (max-width: 768px) {
          .banner-slide, .banner-image {
            height: 250px;
          }
          .banner-content {
            left: 20px;
            bottom: 20px;
            padding: 12px 20px !important;
            min-width: 200px;
          }
          .banner-title {
            font-size: 20px;
          }
          .banner-subtitle {
            font-size: 14px;
          }
          .banner-rating {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
}

// BannerSkeleton component for initial load
function BannerSkeleton() {
  return (
    <div className="banner-carousel mb-4" style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="skeleton banner-slide" style={{ width: '90vw', maxWidth: 1200, borderRadius: 32 }} />
    </div>
  );
}


// ── Types ──────────────────────────────────────────────


const BADGE_STYLES = {
  new: "badge bg-primary text-white",
  free: "badge bg-success text-white",
  updated: "badge bg-warning text-dark",
  leaving: "badge bg-danger text-white",
};

function Chip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`category-chip${active ? " active" : ""}`}
      type="button"
    >
      {label}
    </button>
  );
}


function ExperienceCard({ app }) {
  const { getPlatformIcon, getPlatformDisplayName } = usePlatforms();
  const platformName = app.platform ? getPlatformDisplayName(app.platform) : null;
  const platformIconUrl = app.platform ? getPlatformIcon(app.platform) : null;
  const formattedRating =
    typeof app.rating === "number" && Number.isFinite(app.rating) ? app.rating.toFixed(1) : "—";
  const formattedTotalRatings =
    typeof app.totalRatings === "number" && Number.isFinite(app.totalRatings)
      ? app.totalRatings.toLocaleString()
      : null;
  const formattedGenres = Array.isArray(app.genre)
    ? app.genre.filter(Boolean).join(", ")
    : app.genre
      ? String(app.genre)
      : "Unknown";
  const formattedPrice = (() => {
    const amount = app?.price?.amount;
    const currency = app?.price?.currency;
    if (typeof amount !== "number" || !Number.isFinite(amount)) return null;
    if (amount === 0) return "Free";
    const symbol = currency === "USD" || !currency ? "$" : `${currency} `;
    return `${symbol}${amount.toFixed(2)}`;
  })();
  const genreSlug = Array.isArray(app.genre) && app.genre.length > 0 
    ? generateSlug(app.genre[0]) 
    : app.genre && typeof app.genre === 'string' 
      ? generateSlug(app.genre) 
      : "app";
  const nameSlug = generateSlug(app.title);
  const appLink = `/xr/${genreSlug}/${nameSlug}?id=${app.id || app.item_id}`;

  return (
    <Link to={appLink} className="card shadow-sm border-0 mb-3" style={{ width: 260, textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="position-relative">
        <img
          src={app.thumbnail}
          alt={app.title}
          className="card-img-top"
          style={{ aspectRatio: "16/10", objectFit: "cover" }}
        />
        {/* {app.badge && (
          <span className={`position-absolute top-0 start-0 m-2 ${BADGE_STYLES[app.badge] || "badge bg-secondary"}`}
            style={{ fontSize: 10, textTransform: "uppercase" }}>
            {app.badge === "leaving" ? "Leaving Soon" : app.badge}
          </span>
        )} */}
      </div>
      <div className="card-body py-2 px-2">
        <h6 className="card-title mb-1 text-truncate" style={{ fontSize: 14, color: '#fff' }}>{app.title}</h6>
        <div className="d-flex align-items-center justify-content-between" style={{ fontSize: 12, gap: 8 }}>
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <span className="text-warning">★</span>
            <span style={{ color: '#E8E8E8' }}>
              {formattedRating}
              {formattedTotalRatings ? (
                <span className="text-muted"> ({formattedTotalRatings})</span>
              ) : null}
            </span>
          </div>
          {formattedPrice ? (
            <span className="text-muted" style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
              {formattedPrice}
            </span>
          ) : null}
        </div>
        {/* Platform — which store (Meta Quest / Steam / Apple Vision) this result
            is from, since a category can mix apps across platforms. Lives down
            here with the rest of the details rather than over the artwork. */}
        {platformName && (
          <div className="d-flex align-items-center gap-1" style={{ fontSize: 12, marginTop: 2, color: '#E8E8E8' }}>
            {platformIconUrl && (
              <img src={platformIconUrl} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
            )}
            {platformName}
          </div>
        )}
        <div className="text-muted text-truncate" title={formattedGenres} style={{ fontSize: 12, marginTop: 2 }}>
          {formattedGenres}
        </div>
      </div>
    </Link>
  );
}


// "See All" target for a category; carries the Explorer's selected platform
// (if any) as `?platform=` so CategoryAllPage opens pre-filtered to it.
const categoryAllPath = (category, platform) =>
  `/categories/${encodeURIComponent(category)}${platform ? `?platform=${encodeURIComponent(platform)}` : ''}`;

function ContentSection({ section, filteredApps, platform }) {
  const navigate = useNavigate();
  const containerRef = useRef();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const CARD_WIDTH = 260;
  const GAP = 8;

  const updateScrollState = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    // Use a small delay to ensure layout is done
    const timer = setTimeout(updateScrollState, 100);
    window.addEventListener('resize', updateScrollState);
    return () => {
      window.removeEventListener('resize', updateScrollState);
      clearTimeout(timer);
    };
  }, [updateScrollState, filteredApps]);

  // A click while the previous smooth scroll is still animating used to call
  // scrollBy() again on top of it — that second delta is measured from a
  // scrollLeft that's still mid-animation, and a delta scrolled past either
  // end (the exact case when 1-2 cards remain) has nowhere valid to land, so
  // the browser corrects back toward the nearest in-bounds position. That's
  // the "moves a little then snaps back to where it started" symptom. Fixed
  // by (1) computing an absolute, clamped target instead of a relative
  // scrollBy delta, and (2) ignoring further clicks until that scroll
  // settles (native `scrollend`, with a timeout fallback for browsers that
  // predate it), so clicks can't stack mid-animation.
  const isScrollingRef = useRef(false);

  const scroll = (direction) => {
    const el = containerRef.current;
    if (!el || isScrollingRef.current) return;

    const scrollAmount = (CARD_WIDTH + GAP) * 3; // Scroll 3 cards at a time
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const target = Math.min(
      maxScrollLeft,
      Math.max(0, el.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)),
    );

    isScrollingRef.current = true;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      isScrollingRef.current = false;
      el.removeEventListener('scrollend', finish);
    };
    el.addEventListener('scrollend', finish, { once: true });
    // Fallback for browsers without `scrollend` (e.g. Safari) — the smooth
    // scroll itself never takes anywhere near this long.
    setTimeout(finish, 600);

    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  if (filteredApps.length === 0) {
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2 px-4">
          <h5 className="mb-0">{section.title}</h5>
          <button className="see-all-btn" onClick={() => navigate(categoryAllPath(section.title, platform))}>
            See All
          </button>
        </div>
        <p className="text-muted py-4 px-4">No matches for current filters.</p>
      </div>
    );
  }

  // Capitalize only the first letter of the section title
  const formattedTitle = section.title.charAt(0).toUpperCase() + section.title.slice(1).toLowerCase();
  return (
    <div className="mb-5 position-relative">
      <div className="d-flex justify-content-between align-items-center mb-3 px-4">
        <h5 className="mb-0" style={{ fontWeight: 700, fontSize: 18 }}>{formattedTitle}</h5>
        <button className="see-all-btn" onClick={() => navigate(categoryAllPath(section.title, platform))}>
          See All
        </button>
      </div>

      <div className="experience-cards-container position-relative">
        {/* Navigation Arrows overlaying the scrollable area */}
        <button
          className="arrow-btn left"
          onClick={() => scroll('left')}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            opacity: canScrollLeft ? 1 : 0,
            pointerEvents: canScrollLeft ? 'auto' : 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          aria-label="Scroll left"
        >
          &#8592;
        </button>

        <div
          ref={containerRef}
          onScroll={updateScrollState}
          className="d-flex hide-scrollbar"
          style={{
            overflowX: 'auto',
            gap: GAP,
            padding: '0 24px',
            // CSS scroll-snap ('x proximity' + per-card 'start') fought with the
            // arrow buttons' scrollBy(): the browser's snap engine would treat a
            // 3-card scrollBy that landed between snap points as "not close
            // enough" and animate back to the nearest one — the reported
            // "scrolls, then slides back to the original position" bug. The
            // arrows already move by a deliberate, fixed amount, so snap points
            // aren't needed here; dropping them lets scrollBy's own smooth
            // animation finish where it's told to.
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth'
          }}
        >
          {filteredApps.map((app) => (
            <div key={app.id} className="flex-shrink-0" style={{ width: CARD_WIDTH }}>
              <ExperienceCard app={app} />
            </div>
          ))}
          {/* Spacer to allow full scroll of last item */}
          <div className="flex-shrink-0" style={{ width: 16 }} />
        </div>

        <button
          className="arrow-btn right"
          onClick={() => scroll('right')}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            opacity: canScrollRight ? 1 : 0,
            pointerEvents: canScrollRight ? 'auto' : 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          aria-label="Scroll right"
        >
          &#8594;
        </button>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        .arrow-btn {
          background: #23242b;
          color: #fff;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .arrow-btn:hover {
          background: #31323b;
          background: #F6255A;
          transform: translateY(-50%) scale(1.1);
        }
        .arrow-btn:disabled {
          opacity: 0;
          cursor: not-allowed;
        }
        .see-all-btn {
          background: rgba(246,37,90,0.1);
          color: #F6255A;
          border: 1px solid rgba(246,37,90,0.2);
          border-radius: 8px;
          padding: 6px 16px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .see-all-btn:hover {
          background: #F6255A;
          color: #fff;
        }
      `}</style>
    </div>
  );
}


// Loading skeleton for sections
function SectionSkeleton() {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="skeleton" style={{ width: 180, height: 28, borderRadius: 8 }} />
        <div className="skeleton" style={{ width: 80, height: 32, borderRadius: 8 }} />
      </div>
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%' }} />
        <div className="d-flex" style={{ gap: 8 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton" style={{ width: 260, height: 180, borderRadius: 16 }} />
          ))}
        </div>
        <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%' }} />
      </div>
    </div>
  );
}

// Component for displaying global filtered apps with Show More button
function GlobalFilteredList({ apps }) {
  const [visibleCount, setVisibleCount] = useState(16); // Show 16 apps initially
  const [loading, setLoading] = useState(false);

  // Reset visible count when apps array changes
  useEffect(() => {
    setVisibleCount(16);
  }, [apps]);

  const currentApps = apps.slice(0, visibleCount);
  const hasMore = visibleCount < apps.length;

  const handleShowMore = () => {
    setLoading(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + 16, apps.length));
      setLoading(false);
    }, 300);
  };

  if (apps.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No apps found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="global-filtered-list" style={{ padding: "0 24px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0" style={{ color: '#fff', fontWeight: 600 }}>
          Filtered Results ({apps.length})
        </h4>
      </div>

      <div className="row">
        {currentApps.map((app) => (
          <div key={app.id} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
            <ExperienceCard app={app} />
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="text-center mt-4">
          <button
            className="btn"
            onClick={handleShowMore}
            disabled={loading}
            style={{
              background: '#F6255A',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.background = '#e91e63';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.target.style.background = '#F6255A';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading...
              </>
            ) : (
              "Show More"
            )}
          </button>
        </div>
      )}

      {!hasMore && currentApps.length > 0 && (
        <div className="text-center mt-4">
          <p className="text-muted" style={{ fontSize: 14 }}>
            Showing all {apps.length} apps
          </p>
        </div>
      )}
    </div>
  );
}

// Show all sections without lazy loading
function SectionList({ filteredSections, platform }) {
  return (
    <div>
      {filteredSections.map(({ section, apps }) => (
        <ContentSection
          key={section.id}
          section={section}
          filteredApps={apps}
          platform={platform}
        />
      ))}
    </div>
  );
}

// Category chip that navigates to category page
function CategoryChipNav({ label, platform }) {
  const navigate = useNavigate();
  // Capitalize only the first letter
  const formattedLabel = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  return (
    <button
      className={"category-chip"}
      type="button"
      onClick={() => navigate(categoryAllPath(label, platform))}
    >
      {formattedLabel}
    </button>
  );
}

// Sorting options (move to top-level scope for visibility)
const SORT_OPTIONS = [
  { value: "A-Z", label: "A to Z" },
  { value: "Z-A", label: "Z to A" },
];

// Rating options for separate filter
const RATING_OPTIONS = [
  { value: "4+", label: "4+ Stars" },
  { value: "3+", label: "3+ Stars" },
  { value: "2+", label: "2+ Stars" },
];

const PRICE_OPTIONS = [
  { value: "FREE", label: "Free" },
  { value: "PAID", label: "Paid" },
];

export default function CategorySuggestions() {
  // Platforms context
  const { getActivePlatforms, loading: platformsLoading } = usePlatforms();
  
  // Backend API integration state
  const [apiData, setApiData] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  
  // Filter states
  const [category, setCategory] = useState(null);
  const [genre, setGenre] = useState(null);
  const [price, setPrice] = useState(null);
  const [sort, setSort] = useState(null);
  const [rating, setRating] = useState(null);
  const [platform, setPlatform] = useState(null);
  // Confirmed live against /tracker/explorer/category that the backend
  // already honors both of these params (bundle=Apps returns a genuinely
  // filtered, if empty, result on this VR-only catalog; number_of_players
  // changes the returned detail count) - this was purely a frontend gap,
  // these two filters were never actually sent.
  const [bundle, setBundle] = useState(null);
  const [numberOfPlayers, setNumberOfPlayers] = useState(null);

  useEffect(() => {
    setApiLoading(true);
    setApiError(null);

    // Build query parameters from filter states for global filtering
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (genre) params.append('genre', genre);
    if (price) params.append('price', price);
    if (rating) params.append('rating', rating);
    if (sort) params.append('sort', sort);
    if (platform) params.append('platform', platform);
    if (bundle) params.append('bundle', bundle);
    if (numberOfPlayers) params.append('number_of_players', numberOfPlayers);

    const url = params.toString() ? `${CATEGORY_API}?${params.toString()}` : CATEGORY_API;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch category data");
        return res.json();
      })
      .then((data) => {
        setApiData(data);
        setApiLoading(false);
      })
      .catch((err) => {
        setApiError(err.message || "Unknown error");
        setApiLoading(false);
      });
  }, [category, genre, price, rating, sort, platform, bundle, numberOfPlayers]);
  const [showFilters, setShowFilters] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { filterSections } = useFilters();
  const filterButtonRef = useRef(null);

  // /tracker/all_filters' "Category" section (id "bundle" — the API's display
  // name collides with, but isn't, this page's own "category" concept) is a
  // content-type filter (Games vs. Apps) — a completely different thing from
  // this page's own category chips (Action, Adventure, Strategy...), which
  // drive the separate `category` state/API param below. It used to feed the
  // exact same `category` param into /tracker/explorer/category by mistake,
  // which has no category named "Games"/"Apps", so Apply silently did
  // nothing. Confirmed live against the API that the backend genuinely
  // filters on `bundle` (and on `number_of_players`, the section below it) -
  // this is a real, working filter, just mislabeled and mis-wired. Relabel
  // it here (only for this page) and wire it to its own `bundle` state
  // instead of colliding with `category`.
  const filterPanelSections = useMemo(
    () => filterSections.map((section) => (section.id === "bundle" ? { ...section, title: "App Type" } : section)),
    [filterSections],
  );

  const hasFilters = Boolean(category || genre || price || rating || platform || sort || bundle || numberOfPlayers);

  // Platform alone shouldn't collapse the category-divided horizontal-scroll
  // sections into one flat combined list — `filteredSections` already narrows
  // each section down to the selected platform (and category/rating), so the
  // sectioned layout stays correct. Only the *other* filters (which the flat
  // `GlobalFilteredList` is built for) should switch views.
  const hasNonPlatformFilters = Boolean(category || genre || price || rating || sort || bundle || numberOfPlayers);

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const clearFilters = useCallback(() => {
    setCategory(null);
    setGenre(null);
    setPrice(null);
    setSort(null);
    setRating(null);
    setPlatform(null);
    setBundle(null);
    setNumberOfPlayers(null);
  }, []);

  // FilterPanel no longer renders its own Platform section (every caller now
  // owns a platform tab row outside it) — this is that row for Category
  // Explorer, matching the single-select-with-clear-on-reclick pattern used
  // by PlatformTabs elsewhere (e.g. the dashboard's "Most Tracked Games").
  const handlePlatformToggle = (platformId) => {
    setPlatform((current) => (current === platformId ? null : platformId));
  };

  const handleApplyFilters = (sel) => {
    // No `setCategory` here: category (Action/Adventure/...) is driven only by
    // the page's own chip row - applying other panel filters must not clobber
    // whichever chip the user has selected.
    setGenre(sel.genre ? (Array.isArray(sel.genre) ? sel.genre[0] : sel.genre) : null);
    setPrice(sel.price ? (Array.isArray(sel.price) ? sel.price[0] : sel.price) : null);
    setRating(sel.rating ? (Array.isArray(sel.rating) ? sel.rating[0] : sel.rating) : null);
    setPlatform(sel.platform ? (Array.isArray(sel.platform) ? sel.platform[0] : sel.platform) : null);
    setBundle(sel.bundle ? (Array.isArray(sel.bundle) ? sel.bundle[0] : sel.bundle) : null);
    setNumberOfPlayers(
      sel.number_of_players
        ? (Array.isArray(sel.number_of_players) ? sel.number_of_players[0] : sel.number_of_players)
        : null,
    );

    const selectedSort = sel.sort || sel.sort_by;
    setSort(selectedSort ? (Array.isArray(selectedSort) ? selectedSort[0] : selectedSort) : null);

    setShowFilters(false);
  };


  // Map API data to UI structure with sorting
  const filteredSections = useMemo(() => {
    if (!apiData || !apiData.data || !apiData.details) return [];
    // Map API data to UI sections
    let sections = apiData.data.map((catObj) => {
      // Each section: { section, apps }
      let apps = (catObj.game_ids || []).map((id) => {
        const detail = apiData.details[id];
        const ratingAvg =
          detail?.rating && typeof detail.rating === "object"
            ? detail.rating.average
            : detail?.rating;
        const totalRatings =
          detail?.rating && typeof detail.rating === "object"
            ? detail.rating.total_rating
            : undefined;
        return detail
          ? {
            id,
            title: detail.name,
            thumbnail: detail.image_url || detail.image,
            rating:
              typeof ratingAvg === "number" && Number.isFinite(ratingAvg)
                ? Number(ratingAvg.toFixed(1))
                : undefined,
            totalRatings:
              typeof totalRatings === "number" && Number.isFinite(totalRatings) ? totalRatings : undefined,
            category: catObj.category,
            genre: detail.genre, // array from API
            price: detail.price,
            platform: detail.platform,
            badge: undefined,
          }
          : null;
      }).filter(Boolean);

      // Genre and price filters are handled by backend API, no need to filter again here

      // Apply rating filter
      if (rating) {
        const minRating = parseFloat(rating.replace("+", ""));
        apps = apps.filter(app => (app.rating || 0) >= minRating);
      }

      // Apply platform filter
      if (platform) {
        apps = apps.filter(app => app.platform && app.platform.toLowerCase() === platform.toLowerCase());
      }

      // Sorting logic for apps
      if (sort === "A-Z") {
        apps = apps.slice().sort((a, b) => a.title.localeCompare(b.title));
      } else if (sort === "Z-A") {
        apps = apps.slice().sort((a, b) => b.title.localeCompare(a.title));
      }
      return { section: { id: catObj.category, title: catObj.category }, apps };
    });
    // Filtering by category (if set)
    if (category) {
      sections = sections.filter((s) => s.section.title === category);
    }
    // Sorting and other filters can be added here if API provides data
    return sections;
  }, [apiData, category, sort, rating, platform]);

  // Create global filtered apps list for when filters are applied
  const globalFilteredApps = useMemo(() => {
    if (!hasFilters || !apiData || !apiData.data || !apiData.details) return [];
    
    let allApps = [];
    
    // Collect all apps from all categories - backend has already applied genre and price filters
    apiData.data.forEach((catObj) => {
      const apps = (catObj.game_ids || []).map((id) => {
        const detail = apiData.details[id];
        const ratingAvg =
          detail?.rating && typeof detail.rating === "object"
            ? detail.rating.average
            : detail?.rating;
        const totalRatings =
          detail?.rating && typeof detail.rating === "object"
            ? detail.rating.total_rating
            : undefined;
        return detail
          ? {
              id,
              title: detail.name,
              thumbnail: detail.image_url || detail.image,
              rating:
                typeof ratingAvg === "number" && Number.isFinite(ratingAvg)
                  ? Number(ratingAvg.toFixed(1))
                  : undefined,
              totalRatings:
                typeof totalRatings === "number" && Number.isFinite(totalRatings) ? totalRatings : undefined,
              category: catObj.category,
              genre: detail.genre, // array from API
              price: detail.price,
              platform: detail.platform,
              badge: undefined,
            }
          : null;
      }).filter(Boolean);
      
      allApps = allApps.concat(apps);
    });

    
    // Backend handles genre filter, but price filter might need frontend fallback
    // Apply price filter on frontend as fallback if backend doesn't handle combined filters properly
    if (price && typeof price === 'string' && price.includes('-')) {
      // Handle price range format like "0-10"
      const [minPrice, maxPrice] = price.split('-').map(p => parseFloat(p.trim()));
      const filteredApps = [];
      const rejectedApps = [];
      
      allApps.forEach((app) => {
        if (!app?.price || typeof app.price.amount !== "number") {
          rejectedApps.push({ title: app.title, reason: 'no_price_data', price: app.price });
          return;
        }
        const appPrice = app.price.amount;
        if (appPrice >= minPrice && appPrice <= maxPrice) {
          filteredApps.push(app);
        } else {
          rejectedApps.push({ title: app.title, reason: 'price_out_of_range', price: appPrice, range: `${minPrice}-${maxPrice}` });
        }
      });
      
      allApps = filteredApps;
    } else if (price && typeof price === 'string' && price.includes('+')) {
      const minPrice = parseFloat(price.replace('+', '').trim());
      allApps = allApps.filter((app) => app?.price && typeof app.price.amount === "number" && app.price.amount >= minPrice);
    } else if (price && (price.toUpperCase() === "FREE" || price.toLowerCase() === "free")) {
      allApps = allApps.filter((app) => app?.price && typeof app.price.amount === "number" && app.price.amount === 0);
    } else if (price && (price.toUpperCase() === "PAID" || price.toLowerCase() === "paid")) {
      allApps = allApps.filter((app) => app?.price && typeof app.price.amount === "number" && app.price.amount > 0);
    }
    
    if (rating) {
      const minRating = parseFloat(rating.replace("+", ""));
      allApps = allApps.filter(app => (app.rating || 0) >= minRating);
    }

    if (platform) {
      allApps = allApps.filter(app => app.platform && app.platform.toLowerCase() === platform.toLowerCase());
    }

    // Apply sorting
    if (sort === "A-Z") {
      allApps = allApps.slice().sort((a, b) => a.title.localeCompare(b.title));
    } else if (sort === "Z-A") {
      allApps = allApps.slice().sort((a, b) => b.title.localeCompare(a.title));
    }

    return allApps;
  }, [hasFilters, apiData, price, rating, sort, platform]);

  const topRatedBannerApps = useMemo(() => {
    if (!apiData || !apiData.data || !apiData.details) return [];

    const parseRating = (raw) => {
      if (raw === null || raw === undefined) return null;
      if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
      if (typeof raw === "string") {
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : null;
      }
      if (typeof raw === "object") {
        const avg = raw?.average;
        if (typeof avg === "number" && Number.isFinite(avg)) return avg;
      }
      return null;
    };

    const getTotalRatings = (raw) => {
      if (!raw || typeof raw !== "object") return null;
      const tr = raw?.total_rating;
      return typeof tr === "number" && Number.isFinite(tr) ? tr : null;
    };

    const winnersByCategory = [];

    for (const catObj of apiData.data) {
      const catName = catObj?.category || "Unknown";
      const ids = Array.isArray(catObj?.game_ids) ? catObj.game_ids : [];
      let best = null;

      for (const id of ids) {
        if (!id) continue;
        const detail = apiData.details[id];
        if (!detail) continue;

        const ratingRaw = detail.rating;
        const avg = parseRating(ratingRaw);
        const total = getTotalRatings(ratingRaw) ?? 0;
        if (avg === null) continue;

        const candidate = {
          id,
          title: detail.name || "Untitled",
          thumbnail: detail.image_url || detail.image,
          rating: Number(avg.toFixed(1)),
          totalRatings: total,
          category: catName,
          genre: detail.genre,
          price: detail.price,
          badge: undefined,
          _totalRatings: total,
        };

        if (
          !best ||
          candidate.rating > best.rating ||
          (candidate.rating === best.rating && candidate._totalRatings > best._totalRatings) ||
          (candidate.rating === best.rating &&
            candidate._totalRatings === best._totalRatings &&
            String(candidate.title).localeCompare(String(best.title)) < 0)
        ) {
          best = candidate;
        }
      }

      if (best?.thumbnail) winnersByCategory.push(best);
    }

    // If the same game wins multiple categories, keep the strongest instance.
    const deduped = new Map();
    for (const app of winnersByCategory) {
      const existing = deduped.get(app.id);
      if (
        !existing ||
        app.rating > existing.rating ||
        (app.rating === existing.rating && (app._totalRatings ?? 0) > (existing._totalRatings ?? 0))
      ) {
        deduped.set(app.id, app);
      }
    }

    return Array.from(deduped.values())
      .sort((a, b) => {
        if ((b.rating ?? -Infinity) !== (a.rating ?? -Infinity)) return (b.rating ?? -Infinity) - (a.rating ?? -Infinity);
        return String(a.title).localeCompare(String(b.title));
      })
      .slice(0, 8)
      .map(({ _totalRatings, ...rest }) => rest);
  }, [apiData]);

  // No `py-4`: Bootstrap's spacing utilities are generated with `!important`,
  // so combining it with `.page-content` here would clobber that class's
  // `padding-top` (the fixed topbar's clearance) down to 1.5rem - not enough
  // to clear an ~80px header, and the heading would render partly behind it.
  return (
    <div className="container-fluid page-content" style={{ background: "#171821", minHeight: "100vh" }}>
      <header className="ca-header" style={{ padding: "0 24px" }}>
        <div className="ca-header__content">
          <div className="ca-header__titles">
            <h1 className="ca-header__title">Category Explorer</h1>
            <p className="ca-header__subtitle">
              The Category Explorer helps users discover and analyze category and genre trends across {(() => {
                const activePlatforms = getActivePlatforms();
                if (platformsLoading || activePlatforms.length === 0) {
                  return 'multiple VR platforms';
                }
                return activePlatforms.map(p => p.display_name).join(' and ');
              })()} stores in real time.
              It provides deep insights into how different categories perform, the top apps or games associated with them, and their download and revenue impact.
            </p>
          </div>
          <div className="ca-header__actions">
          </div>
        </div>
      </header>

      <div style={{ padding: "0 24px" }}>
        {/* Platform filter, same shared row used on the home screen (single-select,
            clicking the active platform again clears back to "all"). */}
        <div style={{ marginBottom: 24 }}>
          <PlatformTabs
            variant="pill"
            showDivider={false}
            selected={platform ? [platform] : []}
            onToggle={handlePlatformToggle}
          />
        </div>

        {/* Show category chips whenever the sectioned layout is still in play
            (i.e. no non-platform filter forced the flat list) and apiData is loaded */}
        {!hasNonPlatformFilters && apiData && apiData.data && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
            <div className="category-chips-scroll" style={{ display: 'flex', gap: 8, overflowX: "auto", paddingBottom: 8, flex: 1 }}>
              {apiData.data.map((catObj) => (
                <CategoryChipNav key={catObj.category} label={catObj.category} platform={platform} />
              ))}
            </div>
            
            <button
              ref={filterButtonRef}
              className="filter-trigger"
              type="button"
              onClick={() => setShowFilters(true)}
              style={{ flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 5H17M5 9H15M8 13H12" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Filter
            </button>
          </div>
        )}
        
        {/* Filter button - right-aligned on its own once a non-platform filter
            forces the flat list (platform alone keeps the row above, with chips) */}
        {hasNonPlatformFilters && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button
              ref={filterButtonRef}
              className="filter-trigger"
              type="button"
              onClick={() => setShowFilters(true)}
              style={{ flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 5H17M5 9H15M8 13H12" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Filter
            </button>
          </div>
        )}
        
        {/* Applied filters - show below Filter button when filters are applied */}
        {hasFilters && (
          <div className="applied-filters-row" style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4, gap: 8, alignItems: 'center', marginBottom: 24 }}>
            {category && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Category: {category}
                <button className="remove-filter-btn" onClick={() => setCategory(null)}>×</button>
              </span>
            )}
            {genre && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Genre: {genre}
                <button className="remove-filter-btn" onClick={() => setGenre(null)}>×</button>
              </span>
            )}
            {price && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Price: {PRICE_OPTIONS.find(p => p.value === price)?.label || price}
                <button className="remove-filter-btn" onClick={() => setPrice(null)}>×</button>
              </span>
            )}
            {sort && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Sort: {SORT_OPTIONS.find(s => s.value === sort)?.label || sort}
                <button className="remove-filter-btn" onClick={() => setSort(null)}>×</button>
              </span>
            )}
            {rating && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Rating: {RATING_OPTIONS.find(r => r.value === rating)?.label || rating}
                <button className="remove-filter-btn" onClick={() => setRating(null)}>×</button>
              </span>
            )}
            {platform && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Platform: {platform.charAt(0).toUpperCase() + platform.slice(1)}
                <button className="remove-filter-btn" onClick={() => setPlatform(null)}>×</button>
              </span>
            )}
            {bundle && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                App Type: {bundle}
                <button className="remove-filter-btn" onClick={() => setBundle(null)}>×</button>
              </span>
            )}
            {numberOfPlayers && (
              <span className="applied-filter-chip" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Players: {numberOfPlayers}
                <button className="remove-filter-btn" onClick={() => setNumberOfPlayers(null)}>×</button>
              </span>
            )}

            <button
              className="category-clear-btn"
              onClick={clearFilters}
              style={{ marginLeft: 8, flexShrink: 0 }}
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Banner carousel below filter bar - only show when no filters are applied */}
      {!hasFilters && (
        apiLoading ? (
          <BannerSkeleton />
        ) : (
          <BannerCarousel apps={topRatedBannerApps} />
        )
      )}

      <FilterPanel
        open={showFilters}
        sections={filterPanelSections.length > 0 ? filterPanelSections : undefined}
        initialValues={useMemo(() => ({
          genre: genre ? [genre] : [],
          price: price ? [price] : [],
          rating: rating ? [rating] : [],
          platform: platform ? [platform] : [],
          bundle: bundle ? [bundle] : [],
          // "Number of Players" is a radio section (like `sort`), not a
          // checkbox one - FilterPanel compares its value with `sel ===
          // opt.key` directly, so it needs the raw string here, not an array
          // (which never equals a string, so the applied option never showed
          // as selected when reopening the panel).
          number_of_players: numberOfPlayers,
          sort: sort
        }), [genre, price, rating, platform, bundle, numberOfPlayers, sort])}
        onClose={() => setShowFilters(false)}
        onApply={handleApplyFilters}
        anchorRef={filterButtonRef}
        gap={4}
      />
      <AppsTrackingPlanModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        title=" Upgrade your plan to unlock this feature"
      />
      {/* Sections */}
      {apiLoading ? (
        // Show 3 skeleton sections while loading
        <>
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
        </>
      ) : apiError ? (
        <div className="alert alert-danger mt-4">{apiError}</div>
      ) : hasNonPlatformFilters ? (
        <GlobalFilteredList apps={globalFilteredApps} />
      ) : (
        <SectionList filteredSections={filteredSections} platform={platform} />
      )}

      <style>{`
        .ca-header {
          /* No extra top margin: the parent now carries the .page-content class
             (added to clear the fixed topbar), which already provides the
             correct offset. This used to be 40px, partially — and
             insufficiently — standing in for that missing offset; keeping it
             after adding .page-content would double up and push the header
             well below where it should sit. */
          margin-top: 0;
          margin-bottom: 10px;
        }
        .ca-header__title {
          margin: 0;
          color: #ffffff;
          font-family: 'Rubik', sans-serif;
          font-weight: 600;
          font-size: 32px;
          margin-bottom: 8px;
        }
        .ca-header__subtitle {
          color: #E8E8E8;
          font-size: 16px;
          max-width: full;
          line-height: 1.5;
        }
        @media (max-width: 768px) {
          .ca-header {
            margin-top: 0;
          }
          .ca-header__title {
            font-size: 24px;
          }
          .ca-header__subtitle {
            font-size: 14px;
          }
          .ca-header__actions {
            margin-top: 16px;
          }
          .filter-trigger {
            width: auto;
            justify-content: flex-start;
          }
        }
        .ca-header__actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 12px;
        }
        .filter-trigger {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          border-radius: 12px;
          padding: 8px 16px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          height: 44px;
          cursor: pointer;
          fontWeight: 600;
          transition: all 0.2s;
        }
        .filter-trigger:hover {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.2);
        }
        .category-clear-btn {
          background: rgba(255,255,255,0.05);
          border: none;
          color: #e6e6e6;
          padding: 6px 14px;
          borderRadius: 12px;
          fontSize: 14px;
          fontWeight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .category-clear-btn:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }
        .applied-filters-row {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 0;
          flex-wrap: nowrap;
          overflow-x: auto;
          flex: 1;
        }
        @media (max-width: 576px) {
          .applied-filters-row {
            gap: 6px;
            margin-top: 8px;
          }
        }
        .card-title {
           font-size: 14px;
           transition: font-size 0.2s;
        }
        @media (max-width: 576px) {
          .card-title {
            font-size: 13px;
          }
          .card-body {
            padding: 8px !important;
          }
          .ca-header {
            padding: 0 16px !important;
          }
          .hide-scrollbar {
            padding: 0 16px !important;
          }
        }
        .applied-filter-chip {
          background: rgba(246, 37, 90, 0.1);
          color: #F6255A;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .remove-filter-btn {
          background: transparent;
          color: #F6255A;
          border: none;
          font-size: 16px;
          cursor: pointer;
          padding: 0;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .category-filter-bar {
          background: #181a20;
          border-radius: 16px;
          padding: 18px 18px 10px 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          position: relative;
          z-index: 2;
        }
        .category-filter-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }
        .category-chips-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 4px;
          margin-bottom: 8px;
        }
        .category-chip {
          background: #23242b;
          color: #e6e6e6;
          border: none;
          border-radius: 999px;
          padding: 7px 16px;
          font-size: 13px;
          font-weight: 500;
          margin-right: 4px;
          margin-bottom: 2px;
          cursor: pointer;
          transition: background 0.18s, color 0.18s;
          /* Sit in a horizontally-scrolling flex row (.category-chips-scroll) —
             without these, a two-word label like "Role playing" would get
             squeezed by the flex layout and wrap onto a second line instead
             of just letting the row scroll, which is what made pills look
             oversized/inconsistent. */
          white-space: nowrap;
          flex-shrink: 0;
          /* A plain <button> centers text on its font baseline, which can read
             as "pushed up" against the equal top/bottom padding above. Centering
             the label explicitly (and pinning line-height to 1) removes that
             optical offset so the text sits dead-center in the pill. */
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .category-chip.active {
          background: linear-gradient(90deg, #F6255A 0%, #F43F5E 100%);
          color: #fff;
        }
        /* Horizontal scrollbar for the category pill row — back to the
           original neutral gray (the pink accent didn't read well here),
           just taller and rounder than the plain browser default. */
        .category-chips-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.25) rgba(255, 255, 255, 0.05);
        }
        .category-chips-scroll::-webkit-scrollbar {
          height: 10px;
        }
        .category-chips-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 5px;
        }
        .category-chips-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.25);
          border-radius: 5px;
        }
        .category-chips-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.4);
        }
        .category-sort-select {
          background: #23242b;
          color: #e6e6e6;
          border: none;
          border-radius: 8px;
          padding: 7px 16px;
          font-size: 14px;
          margin-left: 10px;
        }
        .category-filter-btn {
          background: #F6255A;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 7px 18px;
          font-size: 14px;
          font-weight: 600;
          margin-left: 10px;
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: background 0.18s;
        }
        .category-filter-btn:hover {
          background: #e91e63;
        }
        .category-clear-btn {
          background: transparent;
          color: #F6255A;
          border: none;
          font-size: 14px;
          font-weight: 500;
          margin-left: 10px;
          cursor: pointer;
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .skeleton {
          background: linear-gradient(90deg, #23242b 25%, #35363c 37%, #23242b 63%);
          background-size: 400% 100%;
          animation: skeleton-shimmer 1.4s ease infinite;
        }
        @keyframes skeleton-shimmer {
          0% { background-position: 100% 0 }
          100% { background-position: 0 0 }
        }
      `}</style>
    </div>
  );
}
